import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { loginRefusalMessage } from 'helpers/loginRefusal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { UserContext } from 'context/userContext';
import { readRememberLogin, saveRememberLogin } from 'lib/authSession';
import { getLocales } from 'services/localesService';
import { ToastMessagesIDs } from 'types/enums';
import { BrandLogo } from 'components/BrandLogo/BrandLogo';
import { readAppliedPlayerTheme, usesLogoTheme } from 'lib/playerTheme';
import styles from './LoginGate.module.css';

type LoginGateProps = {
  from?: string;
};

export function LoginGate({ from = '/' }: LoginGateProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const { t, i18n } = useTranslation();
  const { login } = useContext(UserContext);
  const navigate = useNavigate();
  const [remember, setRemember] = useState(readRememberLogin);
  const [flagPath, setFlagPath] = useState(
    () => `/photos/icons/${localStorage.getItem('lang') ?? 'en'}.png`
  );

  const formSubmitHandler = async (formData: any) => {
    const userInfo = await login(formData.username, formData.password);
    if (formData.username && formData.password && userInfo) {
      if (!userInfo.has_logged_in_before) {
        navigate('/change-password');
        return;
      }
      const target = from && from !== '/login' ? from : '/';
      navigate(target.startsWith('/') ? target : '/');
      return;
    }
    toast(loginRefusalMessage(t, i18n.language), {
      toastId: ToastMessagesIDs.LOGIN_ERROR,
    });
    reset();
  };

  return (
    <div className={styles.page}>
      <div className={styles.languageRow}>
        <img src={flagPath} alt="" className={styles.flag} />
        <select
          className={styles.languageSelect}
          value={i18n.language}
          onChange={(e) => {
            const newLang = e.target.value;
            setFlagPath(`/photos/icons/${newLang}.png`);
            localStorage.setItem('lang', newLang);
            i18n.changeLanguage(newLang);
          }}
        >
          {getLocales().map((locale) => (
            <option value={locale.code} key={locale.code}>
              {locale.fullName}
            </option>
          ))}
        </select>
      </div>

      {/* tottalo's login gate has always set the name in type, not shown its
          logo — keep it that way; every other palette gets the drawn mark. */}
      {usesLogoTheme(readAppliedPlayerTheme()) ? (
        <h1 className={styles.brandText}>TOTTALO</h1>
      ) : (
        <BrandLogo variant="login" className={styles.brand} />
      )}

      <div className={styles.card}>
        <h1 className={styles.title}>{t('login_label')}</h1>

        <form
          id="login"
          method="post"
          action="/"
          className={styles.form}
          onSubmit={handleSubmit(formSubmitHandler)}
        >
          <label className={styles.field}>
            <span>{t('username')}</span>
            <input
              type="text"
              id="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className={classNames(errors.username && styles.errorInput)}
              {...register('username', { required: true })}
            />
          </label>
          <label className={styles.field}>
            <span>{t('password')}</span>
            <input
              type="password"
              id="password"
              autoComplete="current-password"
              className={classNames(errors.password && styles.errorInput)}
              {...register('password', { required: true })}
            />
          </label>
          <label className={styles.remember}>
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => {
                setRemember(e.target.checked);
                saveRememberLogin(e.target.checked);
              }}
            />
            <span>{t('remember_login')}</span>
          </label>
          <button type="submit" className={styles.submit}>
            {t('login_label')}
          </button>
        </form>

        <div className={styles.forgotLinks}>
          <span>{t('forgot_username')}</span>
          <span>{t('forgot_password')}</span>
        </div>
      </div>
    </div>
  );
}
